import {Map, List} from 'immutable';
import {QUEUED} from './reducers';
import {arrayize, values, all, any} from './utils';

class TypeAccessor {
  constructor(requests, representations, resources, request, typeName) {
    this.requestUuid = (request) ? request.uuid : null;
    this.resource = resources.get(this.requestUuid, Map()).get(typeName, List());
    this.representations = representations.get(typeName, Map());
  }

  get(id) {
    const idString = (id === undefined) ? this.resource.first() : id.toString();

    return (this.resource.includes(idString)) ?
      this.representations.get(idString, null) :
      null;
  }

  all() {
    return this.resource.map((id) => {
      return this.representations.get(id);
    });
  }

  filter(filterParamsOrId) {
    const filterParamsIsId = typeof filterParamsOrId === 'string' || typeof filterParamsOrId === 'number';
    const filterParams = (filterParamsIsId) ? {id: filterParamsOrId} : filterParamsOrId;

    const found = this.representations.filter((representation) => {
      const matches = Object.keys(filterParams).map((key) => {
        return filterParams[key] === representation[key];
      });

      return all(matches);
    });

    return found.filter((representation) => {
      return this.resource.includes(representation.id.toString());
    });
  }

  find(findParams) {
    const filtered = this.filter(findParams);

    return (filtered.size === 1) ? filtered.first() : null;
  }
}

class RequestsAccessor {
  constructor(requests, status, errors) {
    this.requests = requests;
    this.status = status;
    this.errors = errors;

    this.meansDone = [200, 201, 204];
    this.meansFailed = [500, 502, 503, 504];
  }

  _requestsWithStatus(requestsOrAll, status) {
    const statusChecker = (typeof status === 'function') ? status : (v) => v === status;
    const requests = (requestsOrAll === true) ? values(this.requests) : arrayize(requestsOrAll) || [];

    return requests.map((request) => {
      return statusChecker(this.status.get((request) ? request.uuid : null)) && !!~values(this.requests).indexOf(request);
    });
  }

  isQueued(request) {
    if (!request) {
      return false;
    }

    return all(this._requestsWithStatus([request], QUEUED));
  }

  allQueued(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, QUEUED));
  }

  anyQueued(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, QUEUED));
  }

  hasErrors(request) {
    if (!request) {
      return false;
    }

    return this.allHaveErrors([request]);
  }

  errorsFor(request) {
    return (this.hasErrors(request)) ? this.errors.get(request.uuid, Map()) : Map();
  }

  allHaveErrors(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, 400));
  }

  anyHaveErrors(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, 400));
  }

  isDone(request) {
    if (!request) {
      return false;
    }

    return this.allDone([request]);
  }


  allDone(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, (status) => {
      return ~this.meansDone.indexOf(status);
    }));
  }

  anyDone(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, (status) => {
      return ~this.meansDone.indexOf(status);
    }));
  }

  isNotFound(request) {
    if (!request) {
      return false;
    }

    return this.allNotFound([request]);
  }

  allNotFound(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, 404));
  }

  anyNotFound(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, 404));
  }

  isNotAllowed(request) {
    if (!request) {
      return false;
    }

    return this.allNotAllowed([request]);
  }

  allNotAllowed(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, 403));
  }

  anyNotAllowed(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, 403));
  }

  hasNoContent(request) {
    if (!request) {
      return false;
    }

    return this.allNoContent([request]);
  }

  allNoContent(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, 204));
  }

  anyNoContent(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, 204));
  }

  hasFailed(request) {
    if (!request) {
      return false;
    }

    return this.allFailed([request]);
  }

  allFailed(requestsOrAll) {
    return all(this._requestsWithStatus(requestsOrAll, (status) => {
      return ~this.meansFailed.indexOf(status);
    }));
  }

  anyFailed(requestsOrAll) {
    return any(this._requestsWithStatus(requestsOrAll, (status) => {
      return ~this.meansFailed.indexOf(status);
    }));
  }
}

export default function (params) {
  const {
    requests,
    representations,
    resources,
    status,
    errors,
  } = params;

  return {
    type(...args) {
      return new TypeAccessor(requests, representations, resources, ...args);
    },
    requests: new RequestsAccessor(requests, status, errors),
    _args: params,
  };
}



// WEBPACK FOOTER //
// ./app/javascript/requests/ResourcesManager.js